import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import { fetchPublicProfile, type PublicProfileLookup } from "@/lib/publicProfile";
import { SeoHead } from "@/components/SeoHead";
import { safeHref } from "@/lib/safeHref";

export default function PublicProfile() {
  const { username = "" } = useParams<{ username: string }>();
  const [state, setState] = useState<{ status: "loading" | "ok" | "missing"; profile?: PublicProfileLookup }>({
    status: "loading",
  });


  useEffect(() => {
    let alive = true;
    setState({ status: "loading" });
    fetchPublicProfile(username)
      .then((p) => {
        if (!alive) return;
        setState(p ? { status: "ok", profile: p } : { status: "missing" });
      })
      .catch(() => alive && setState({ status: "missing" }));
    return () => {
      alive = false;
    };
  }, [username]);

  const seo = (
    <SeoHead
      title={`@${username} · Excerptus`}
      description={`Public profile for @${username} on Excerptus.`}
      noindex
    />
  );

  if (state.status === "loading") {
    return (
      <>
        {seo}
        <div className="flex min-h-dvh items-center justify-center bg-background text-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  if (state.status === "missing") {
    return (
      <>
        {seo}
        <div className="flex min-h-dvh flex-col items-center justify-center gap-2 bg-background px-6 text-center text-foreground">
          <h1 className="text-2xl font-semibold">Profile not found</h1>
          <p className="text-muted-foreground">
            @{username} hasn't published a public profile.
          </p>
          <Link to="/" className="mt-4 text-sm text-primary underline">
            Back to Excerptus
          </Link>
        </div>
      </>
    );
  }

  const p = state.profile!;
  const initial = (p.display_name ?? p.username).slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {seo}
      <div className="mx-auto flex max-w-xl flex-col items-center gap-6 px-6 py-16">
        <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-4xl font-semibold text-muted-foreground">
          {p.avatar_url ? (
            <img src={p.avatar_url} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          ) : (
            <span>{initial}</span>
          )}
        </div>

        <div className="space-y-1 text-center">
          {p.display_name ? (
            <h1 className="text-2xl font-semibold">{p.display_name}</h1>
          ) : null}
          <p className="text-sm text-muted-foreground">@{p.username}</p>
        </div>

        {p.bio ? (
          <p className="max-w-md whitespace-pre-line text-center text-foreground/90">
            {p.bio}
          </p>
        ) : null}

        {p.social_links.length > 0 ? (
          <div className="flex w-full max-w-md flex-col gap-2">
            {p.social_links.map((l, i) => {
              const safe = safeHref(l.url);
              if (!safe) return null;
              return (
                <a
                  key={`${l.url}-${i}`}
                  href={safe}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-center justify-between rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-accent"
                >
                  <span>{l.label}</span>
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </a>
              );
            })}
          </div>
        ) : null}

        <Link
          to={`/?ref=${encodeURIComponent(p.username)}`}
          className="mt-8 text-xs text-muted-foreground hover:text-foreground"
        >
          Powered by Excerptus
        </Link>
      </div>
    </div>
  );
}
